/**
 * `/settings/contact` (#581): one way to reach the people behind DaVinci - a `mailto:` button. Exempt from the consent
 * gate (consent-gate.tsx): asking about consent or one's data must not need consent.
 */

import { useLocale, useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

export const CONTACT_EMAIL = 'ben.rapoport2002@gmail.com';

const messages = defineMessages({
  en: {
    title: 'Contact us',
    intro: 'A real person reads every message.',
    writeTitle: 'Write to us',
    writeIntro: 'Hebrew or English, either is fine.',
    answer: 'We answer within 30 days, usually much sooner.',
    topicsTitle: 'What we can help with',
  },
  he: {
    title: 'יצירת קשר',
    intro: 'אנשים אמיתיים קוראים כל הודעה.',
    writeTitle: 'לכתוב לנו',
    writeIntro: 'אפשר לכתוב בעברית או באנגלית.',
    answer: 'התשובה מגיעה תוך 30 יום, ובדרך כלל הרבה יותר מהר.',
    topicsTitle: 'במה אפשר לעזור',
  },
});

/** The board's four topics. Rows of information: each opens the same mail draft as the button above. */
const TOPICS: readonly { readonly id: string; readonly en: { name: string; what: string }; readonly he: { name: string; what: string } }[] = [
  { id: 'question', en: { name: 'A question about DaVinci', what: 'how something works, or why a check said what it said' }, he: { name: 'שאלה על DaVinci', what: 'איך משהו עובד, או למה הבדיקה אמרה מה שאמרה' } },
  { id: 'broken', en: { name: 'Something is broken', what: 'a page that will not load, gets stuck or looks wrong' }, he: { name: 'תקלה', what: 'משהו לא נטען, נתקע או נראה שבור' } },
  { id: 'data', en: { name: 'My data', what: 'what is kept, fixing something wrong, or asking to inspect it' }, he: { name: 'הנתונים שלי', what: 'מה נשמר, לתקן משהו לא נכון, או לבקש לעיין בהם' } },
  { id: 'privacy', en: { name: 'Consent and privacy', what: 'what is sent, where and why' }, he: { name: 'הסכמה ופרטיות', what: 'מה נשלח, לאן ולמה' } },
];

export function ContactPage(): React.JSX.Element {
  const t = useMessages(messages);
  const { locale } = useLocale();
  return (
    <div className="page">
      <h2>{t.title}</h2>
      <p>{t.intro}</p>
      <section className="settings-card settings-card-bar" data-course="plum" aria-labelledby="write-heading">
        <h3 id="write-heading">{t.writeTitle}</h3>
        <p className="contact-line">{t.writeIntro}</p>
        <a className="settings-button settings-mail" href={`mailto:${CONTACT_EMAIL}`}>
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="M3 7l9 6 9-6" />
          </svg>
          <bdi dir="ltr">{CONTACT_EMAIL}</bdi>
        </a>
        <p className="contact-answer">{t.answer}</p>
      </section>
      <section className="settings-card" aria-labelledby="topics-heading">
        <h3 id="topics-heading">{t.topicsTitle}</h3>
        <ul className="contact-topics">
          {TOPICS.map((topic) => (
            <li key={topic.id}>
              <a className="contact-topic" data-topic={topic.id} href={`mailto:${CONTACT_EMAIL}`}>
                <strong>{topic[locale].name}</strong>
                <span>{topic[locale].what}</span>
              </a>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
